import React, { useEffect, useState } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Text from '../../shared/Text';
import ScreenWrapper from '../../shared/ScreenWrapper';
import styled from 'styled-components';
import styles from '../../../utility/styles';
import Button from '../../shared/Button';
import Input from '../../shared/Input';
import { post } from '../../../utility/api';
import { debounce } from 'lodash';
import Spinner from '../../shared/Spinner';
import MessageToast from '../../shared/MessageToast';

const Check = ({ isChecked }) => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: isChecked ? 'block' : 'none' }}>
        <path d="M5.83051 11.1795L12.2811 3.1407C12.6252 2.71186 13.2494 2.64626 13.6751 2.99419C14.1009 3.34211 14.1671 3.97181 13.8229 4.40065L6.77353 13.1856C6.4294 13.6145 5.80529 13.6801 5.38976 13.3402L2.37066 11.0641C1.9449 10.7162 1.87872 10.0865 2.22284 9.65766C2.56696 9.22882 3.19107 9.16322 3.60661 9.50311L5.83051 11.1795Z" fill="white" />
    </svg>
)

const Link = styled.a`
    color: ${styles.colors.lightGray};
    text-decoration: underline;
    font-family: ${styles.fonts.geist};
    font-size: 14px;
    line-height: 20px;
`

const BSRow = styled(Row)`
    margin-bottom: 56px;
    @media only screen and (max-width: 768px) {
        margin-bottom: 24px;
    }
`

const LinkButton = styled.button`
    background: none;
    padding: 16px;
    border-radius: 8px;
    border: ${({ selected }) => selected ? `2px solid ${styles.colors.premiumCopy}` : `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`};
	color: ${({ disabled }) => (disabled ? styles.colors.lightGray : styles.colors.white)};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    transition: all 0.3s ease;
    text-align: left;
    &:hover, &:focus {
        color: ${({ disabled }) => (disabled ? styles.colors.lightGray : styles.colors.white)};
        border: ${({ selected }) => selected ? `2px solid ${styles.colors.premiumCopy}` : `1px solid ${styles.colors.premiumCopy}`};
    }
`

const StyledButton = styled.a`
    min-width: 330px;
    max-width: 330px;
    margin: 0 auto;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 8px 16px;
	border-radius: 10px;
	box-shadow: 4.5px 6px 12px rgba(0, 0, 0, 0.16);
	background-color: ${styles.colors.blue};
	border: none;
	font-family: ${styles.fonts.geist};
	font-weight: 600;
    margin-top: -48px;
	color: ${styles.colors.white};
    cursor: pointer;
    text-decoration: none;
    @media only screen and (max-width: 768px) {
        margin-top: -12px;
        font-size: 14px;
    }
    &:hover, &:focus {
        color: ${styles.colors.white};
        text-decoration: none;
    }
`

const StyledButtonDisabled = styled.button`
    min-width: 330px;
    max-width: 330px;
    margin: 0 auto;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 8px 16px;
	border-radius: 10px;
	box-shadow: 4.5px 6px 12px rgba(0, 0, 0, 0.16);
	background-color: ${styles.colors.mediumGray};
	border: none;
	font-family: ${styles.fonts.geist};
	font-weight: 600;
    margin-top: -48px;
	color: ${styles.colors.lightGray};
    cursor: not-allowed;
    @media only screen and (max-width: 768px) {
        margin-top: -12px;
        font-size: 14px;
    }
    &:hover, &:focus {
        color: ${styles.colors.lightGray};
    }
`

const SkeletonBox = styled.div`
    background-color: ${styles.colors.mediumGray};
    border-radius: 4px;
    animation: pulse 1.5s ease-in-out infinite;
    
    @keyframes pulse {
        0% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
        100% {
            opacity: 1;
        }
    }
`


const FsdBackLink = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	color: ${styles.colors.lightGray};
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
	margin-bottom: 24px;
	transition: color 0.15s ease;
	&:hover, &:focus, &:active {
		color: ${styles.colors.whiteGray};
		text-decoration: none;
	}
	&:focus {
		outline: none;
	}
	&:focus-visible {
		outline: 2px solid rgba(16, 171, 255, 0.5);
		outline-offset: 3px;
		border-radius: 6px;
	}
`

const FsdBackArrow = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 6px;
	background: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
	color: ${styles.colors.whiteGray};
	font-size: 14px;
	transition: background 0.15s ease;
	${FsdBackLink}:hover & {
		background: ${styles.colors.mediumGray};
	}
`

const CustomizeLink = styled.span`
    color: ${styles.colors.lightGray};
    font-size: 14px;
    text-decoration: none;
    cursor: pointer;
    font-family: ${styles.fonts.geist};
    
    &:hover {
        color: ${styles.colors.white};
    }
`



const StyledContainer = styled(Container)`
    padding: 0 24px;
    margin-top: ${({ isWebView }) => (isWebView ? '24px' : '72px')};
    margin-bottom: 72px;
`;

const AddOnsWrapper = styled.div`
    padding: 4px 8px;
    background-color: ${styles.colors.forest};
    border-radius: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    align-self: flex-start;
    margin-bottom: 12px;
`;

const Span = styled.span`
    color: ${({ color }) => (color ? color : `${styles.colors.white}`)};
    font-weight: 600;
`;

const PricesWrapper = styled.div`
    padding: 16px 24px;
    display: flex;
    align-items: stretch;
    justify-content: center;
    border: 1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10));
    border-radius: 8px;
    margin-bottom: 40px;
    @media only screen and (max-width: 768px) {
        margin-bottom: 24px;
        border-left: none;
        border-right: none;
        border-radius: 0;
        padding: 16px 8px;
    }
`;

const ButtonsWrapper = styled(Row)`
    margin-top: 16px;
    margin-bottom: 56px;
    @media only screen and (max-width: 768px) {
        margin-bottom: 24px;
    }
`

const StyledRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1;
`

const DiscountWrapper = styled.div`
    position: relative;
    margin-top: 52px;
    margin-left: -1px;
    margin-right: -1px;
    width: calc(100% + 2px);
    @media only screen and (max-width: 768px) {
        margin-top: 48px;
    }
`

const DiscountContainer = styled.div`
    position: relative;
    border: 1px solid ${styles.colors.founders};
    border-radius: 8px;
    padding: 16px;
    padding-top: 16px;
    background-color: ${styles.colors.background};
    z-index: 2;
    @media only screen and (max-width: 768px) {
        padding: 12px;
        padding-top: 12px;
    }
`

const DiscountTab = styled.div`
    position: absolute;
    top: -36px;
    left: 0;
    right: 0;
    width: 100%;
    background-color: ${styles.colors.founders};
    color: ${styles.colors.black};
    padding: 6px 12px;
    padding-bottom: 12px;
    border: 1px solid ${styles.colors.founders};
    border-radius: 8px 8px 0 0;
    font-weight: bold;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    z-index: 1;
    @media only screen and (max-width: 768px) {
        padding: 6px 12px;
        padding-bottom: 12px;
        font-size: 12px;
        top: -32px;
        gap: 6px;
    }
    svg {
        width: 22px;
        height: 22px;
        fill: ${styles.colors.black};
        flex-shrink: 0;
        @media only screen and (max-width: 768px) {
            width: 18px;
            height: 18px;
        }
    }
`

const TableTextWrapper = styled.div`
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
    text-align: center;
`

const StyledCol = styled.div`
    display: flex;
    flex-direction: column;
    margin-right: 16px;
`

const Pill = styled.div`
    margin: 16px 0;
    display: flex;
    padding: 8px 12px;
    justify-content: center;
    align-items: center;
    align-self: flex-start;
    gap: 4px;
    border-radius: 1000px;
    background: ${styles.colors.premiumBackground};
    pointer-events: none;
`

const SpecialOfferContainer = styled.div`
	display: flex;
	align-items: center;
	padding: 16px 24px 16px 0;
	background-color: ${styles.colors.darkBackground};
	border: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.07));
	border-radius: 8px;
	margin: 0 auto;
	position: relative;
	@media only screen and ${styles.breakpoints.mobileL} {
		flex-direction: column;
		align-items: flex-start;
		padding: 0 24px 16px 24px;
	}
`

const DiscountReflectedText = styled(Text.Caption)`
	color: ${styles.colors.lightGray};
	font-size: 11px;
	margin-top: 8px;
`

const SpecialOfferIconContainer = styled.div`
	padding: 24px;
	background-color: ${styles.colors.founders};
	border-radius: 0 8px 8px 0;
	display: flex;
	align-items: center;
	justify-content: center;
	@media only screen and ${styles.breakpoints.mobileL} {
		border-radius: 0 0 8px 8px;
	}
	svg {
		width: 48px;
		height: 48px;
		fill: #2C323B;
	}
`

const SpecialOfferTextContainer = styled.div`
	margin: 0 24px;
	flex: 1;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	@media only screen and ${styles.breakpoints.mobileL} {
		margin: 24px 0;
	}
`

/**
 * 
 * @param {{ data: {
 *   user: {
 *     monthly_driving: number,
 *     monthly_driving_hours_string: string,
 *     monthly_driving_units: string,
 *     monthly_driving_units_plural: string
 *   },
 *   title: string,
 *   units: string,
 *   units_plural: string,
 *   balance: {
 *     used: number,
 *     available: number
 *   },
 *   pricing_table: [{
 *     qty_low: number,
 *     qty_high: number,
 *     per_credit: number,
 *     savings_pct: number,
 *     savings_per_credit: number
 *   }],
 *   checkout_url: string,
 *   flash_message: {
 *     header: string,
 *     message: string,
 *     error: boolean
 *   },
 *   autoload_status: {
 *     active: boolean,
 *     threshold: number,
 *     quantity: number,
 *     quote: {
 *       qty: number,
 *       quote: number,
 *       quote_stripe: number,
 *       unit_amount: number,
 *       unit_amount_stripe: number,
 *       savings_pct: number,
 *       savings_total: number,
 *       savings_per_credit: number
 *     }
 *   }
 * },
 * button_list: [{
 *   quantity: number,
 *   quantity_str: string,
 *   cost: string,
 *   subtitle: string,
 *   cost_per_unit: string
 * }],
 * isWebView: boolean
 * }} props
 * @returns {JSX.Element}
 */
export default function ManageFSD({ data, isWebView }) {
    const { user, title, units, units_plural, balance, pricing_table, checkout_url, autoload_status, quote: autoload_quote, flash_message, button_list } = data
    // FSD state for purchases
    const [isLoading, setIsLoading] = useState(false)
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false)
    const [toastMessageObject, setToastMessageObject] = useState(flash_message ? {
        header: flash_message.header,
        message: flash_message.message,
        error: flash_message.error
    } : null)
    const [quantity, setQuantity] = useState(quote?.qty || '')
    const [error, setError] = useState('')
    const [quote, setQuote] = useState(autoload_quote)
    const [isCustomized, setIsCustomized] = useState(false)
    // FSD state for autoload
    const [isAutoloadLoading, setIsAutoloadLoading] = useState(false)
    const [isRefill, setIsRefill] = useState(autoload_status?.active)
    const [refillAmount, setRefillAmount] = useState(autoload_status?.quantity)
    const [refillTrigger, setRefillTrigger] = useState(autoload_status?.threshold)
    const [refillData, setRefillData] = useState(autoload_status)
    const [refillError, setRefillError] = useState('')
    const magic_link_auth_key = window.location.pathname.split('/')[2]

    const [checkoutUrl, setCheckoutUrl] = useState(checkout_url)

    const cancelAutoload = async () => {
        setRefillError('')
        try {
            const { data } = await post(`/subscribe/${magic_link_auth_key}/manage/fsd/autoload`, { disabled: true })
            if (data.error) throw data.error
            setRefillData(data)
            setIsRefill(data.active)
            setRefillTrigger(data.threshold)
            setRefillAmount(data.quantity)
        } catch (error) {
            setRefillError('There was an error updating the autoload. Please try again.')
        } finally {
            setIsAutoloadLoading(false)
        }
    }

    const updateAutoload = async () => {
        setRefillError('')
        try {
            const { data } = await post(`/subscribe/${magic_link_auth_key}/manage/fsd/autoload`, { threshold: refillTrigger, quantity: refillAmount })
            if (data.error) throw data.error
            setRefillData(data)
            setRefillTrigger(data.threshold)
            setRefillAmount(data.quantity)
            setIsRefill(data.active)
        } catch (error) {
            setRefillError('There was an error updating the autoload. Please try again.')
        } finally {
            setIsAutoloadLoading(false)
        }
    }

    const generateQuote = async (value) => {
        try {
            const { data } = await post(`/subscribe/${magic_link_auth_key}/manage/fsd/quote`, { quantity: Number(value) })
            if (data.error) throw data.error
            setQuote(data)
        } catch (error) {
            throw error
        }
    }

    const generateCheckoutUrl = async (qty) => {
        setIsCheckoutLoading(true)
        try {
            const { data } = await post(`/subscribe/${magic_link_auth_key}/manage/fsd/checkout`, { quantity: qty })
            if (data.error) throw data.error
            console.log("CHECKOUT URL:", data)
            setCheckoutUrl(data.checkout_url)
        } catch (error) {
            console.error('Checkout URL generation failed:', error)
            setCheckoutUrl(null)
        } finally {
            setIsCheckoutLoading(false)
        }
    }

    const resetQuote = () => {
        setQuote(null)
    }

    const resetCheckoutUrl = () => {
        setCheckoutUrl(null)
        setIsCheckoutLoading(false)
    }

    const hideToast = (obj) => {
        setToastMessageObject(null)
    }

    // value change handlers

    const updateQuantity = ({ target: { value } }) => {
        if (isLoading) return
        setQuantity(value)
    }

    const handleButtonClick = ({ target: { dataset: { quantity }, id } }) => {
        setQuantity(quantity)
        setIsCustomized(false)
    }

    const updateRefillTrigger = ({ target: { value } }) => {
        if (isNaN(Number(value))) return
        setRefillTrigger(Number(value))
    }

    const updateRefillAmount = ({ target: { value } }) => {
        if (isNaN(Number(value))) return
        setRefillAmount(Number(value))
    }

    const updateIsRefill = ({ target: { checked } }) => {
        setIsAutoloadLoading(true)
        setIsRefill(checked)
    }

    // effect handlers

    useEffect(() => {
        if (user?.monthly_driving && typeof user.monthly_driving === 'number') {
            setQuantity(user.monthly_driving);
        }
    }, []);

    // Clear quote and checkout data immediately when quantity changes
    useEffect(() => {
        resetQuote();
        resetCheckoutUrl();
    }, [quantity]);

    // Debounced quote generation (1000ms)
    useEffect(() => {
        const debouncedQuote = debounce(async () => {
            if (isNaN(Number(quantity)) || !quantity) {
                resetQuote()
                resetCheckoutUrl()
                return
            }
            setIsLoading(true)
            setError('')
            try {
                await generateQuote(quantity)
            } catch (error) {
                resetQuote()
                if (error === 'quantity_too_low') {
                    setError(`Please enter at least ${user?.min_purchase}`)
                    return
                }
                if (error === 'quantity_too_high') {
                    setError(`Please enter less than ${user?.max_purchase}`)
                    return
                }
                setError(typeof error === 'string' ? error : 'There was an error generating the quote. Please try again.')
            } finally {
                setIsLoading(false)
            }
        }, 1000)
        debouncedQuote()
        return () => debouncedQuote.cancel()
    }, [quantity])

    // Generate checkout URL when quote matches current quantity
    useEffect(() => {
        if (quote?.qty && quote.qty === Number(quantity)) {
            // console.log('Generating checkout URL for quantity:', Number(quantity))
            generateCheckoutUrl(Number(quantity))
        }
    }, [quote, quantity])

    useEffect(() => {
        const debouncedUpdate = debounce(async () => {
            if (isRefill === refillData?.active) return
            if (isRefill) {
                await updateAutoload()
            } else {
                await cancelAutoload()
            }
        }, 500)
        debouncedUpdate()
        return () => debouncedUpdate.cancel()
    }, [isRefill])

    const pricePerUnit = quote?.unit_amount
    const totalUnits = balance?.available + quote?.qty

    return (
        <ScreenWrapper {...{ isWebView }}>
            <StyledContainer isWebView={isWebView}>
                <MessageToast show={!!toastMessageObject} heading={toastMessageObject?.header} body={toastMessageObject?.message} onHide={() => hideToast(toastMessageObject)} options={{ error: toastMessageObject?.error, style: { top: '88px' } }} />
                <BSRow>
                    <Col>
                        <FsdBackLink href={`/subscribe/${magic_link_auth_key}/manage`}>
                            <FsdBackArrow aria-hidden='true'>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
                            </FsdBackArrow>
                            Membership &amp; Vehicle Connections
                        </FsdBackLink>
                    </Col>
                </BSRow>
                <BSRow>
                    <Col className='d-flex flex-column'>
                        <AddOnsWrapper>
                            <Text.Tiny variant='bold'>ADD-ON</Text.Tiny>
                        </AddOnsWrapper>
                        <Text.Heading style={{ color: styles.colors.forest, marginBottom: '8px' }} type='h2' variant='bold'>{title}</Text.Heading>
                        <Text.Para>
                            Basic FSD tracking is included with your TezLab Pro membership (requires FSD 14). Enhanced tracking runs on some serious horsepower to analyze tons of real-time vehicle telemetry and is available through flexible, pay-as-you-go hourly credits.{' '}
                            <Span>Once purchased, credits do not expire, and are used up only while driving. Lighter drivers spend less, and frequent drivers can save with larger discounted credit bundles. Enhanced tracking supports HW2+ vehicles.</Span>
                        </Text.Para>
                    </Col>
                </BSRow>
                { data?.available_promos?.length > 0 && (
					<Row style={{ marginBottom: '48px' }}>
						<Col>
							<SpecialOfferContainer>
								<SpecialOfferIconContainer>
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M320 64C356.8 64 388.8 84.7 404.9 115.1C437.8 105 475 113 501 139C527 165 535 202.3 524.9 235.1C555.3 251.2 576 283.2 576 320C576 356.8 555.3 388.8 524.9 404.9C535 437.8 527 475 501 501C475 527 437.7 535 404.9 524.9C388.8 555.3 356.8 576 320 576C283.2 576 251.2 555.3 235.1 524.9C202.2 535 165 527 139 501C113 475 105 437.7 115.1 404.9C84.7 388.8 64 356.8 64 320C64 283.2 84.7 251.2 115.1 235.1C105 202.2 113 165 139 139C165 113 202.3 105 235.1 115.1C251.2 84.7 283.2 64 320 64zM256 288C273.7 288 288 273.7 288 256C288 238.3 273.7 224 256 224C238.3 224 224 238.3 224 256C224 273.7 238.3 288 256 288zM416 384C416 366.3 401.7 352 384 352C366.3 352 352 366.3 352 384C352 401.7 366.3 416 384 416C401.7 416 416 401.7 416 384zM401 273C410.4 263.6 410.4 248.4 401 239.1C391.6 229.8 376.4 229.7 367.1 239.1L239 367C229.6 376.4 229.6 391.6 239 400.9C248.4 410.2 263.6 410.3 272.9 400.9L401 273z"/></svg>
								</SpecialOfferIconContainer>
								<SpecialOfferTextContainer>
									<div>
										<Text.Heading type='h4' variant='bold' color={styles.colors.founders}>Special Offer{data?.available_promos?.length > 1 ? 's' : ''}</Text.Heading>
										{data?.available_promos?.map(x=>{
											return (
												<Text.Body key={x.id} color={styles.colors.whiteGray} style={{ marginTop: '4px' }}>{x.discount_badge_text}</Text.Body>
											)
										})}
									</div>
									<DiscountReflectedText>
										Special offer savings are in addition to volume savings and are applied at checkout.
									</DiscountReflectedText>
								</SpecialOfferTextContainer>
							</SpecialOfferContainer>
						</Col>
					</Row>
				)}
                <PricesWrapper>
                    <Row style={{ flex: 1 }}>
                        <Col className='d-flex flex-column'>
                            <StyledRow style={{ flex: 1, padding: '8px 0', borderRadius: '8px', backgroundColor: styles.functions.opacityModifier(styles.colors.superNinjaBlack, 20) }}>
                                <TableTextWrapper>
                                    <Text.Body color={styles.colors.lightGray}>Credits ({units_plural})</Text.Body>
                                </TableTextWrapper>
                                <TableTextWrapper>
                                    <Text.Body>Price/hr of driving</Text.Body>
                                </TableTextWrapper>
                                <TableTextWrapper>
                                    <Text.Body color={styles.colors.forest}>Savings</Text.Body>
                                </TableTextWrapper>
                            </StyledRow>
                            {pricing_table?.map((pricing, index, array) => (
                                <StyledRow key={pricing?.qty_low} style={{ flex: 1, padding: '8px 0' }}>
                                    <TableTextWrapper>
                                        <Text.Body color={styles.colors.lightGray}>{pricing?.qty_low}-{pricing?.qty_high}</Text.Body>
                                    </TableTextWrapper>
                                    <TableTextWrapper>
                                        <Text.Body>{pricing?.per_credit_str}/{units}</Text.Body>
                                    </TableTextWrapper>
                                    <TableTextWrapper>
                                        <Text.Body style={{ opacity: !!pricing?.savings_pct ? 1 : 0, padding: '2px 8px', borderRadius: '4px', backgroundColor: styles.functions.opacityModifier(styles.colors.forest, 20) }} color={styles.colors.forest}>Save {pricing?.savings_pct}%</Text.Body>
                                    </TableTextWrapper>
                                </StyledRow>
                            ))}
                        </Col>
                    </Row>
                </PricesWrapper>
                <BSRow>
                    <Col className='d-flex flex-column align-items-center'>
                        {!!user?.monthly_driving && (
                            <Text.Heading className='text-center' type='h6' variant='bold'>You usually drive an average of <Span color={styles.colors.premiumCopy}>{user?.monthly_driving_hours_string}</Span> per month.</Text.Heading>
                        )}
                    </Col>
                </BSRow>
                <ButtonsWrapper>
                    {button_list?.map(button => (
                        <Col xs={6} md={3} key={button?.quantity} className='d-flex p-3'>
                            <LinkButton selected={quantity == button?.quantity} onClick={handleButtonClick} data-quantity={button?.quantity}>
                                <Text.Caption className='mb-1' variant='bold' color="#66bb6a" style={{ fontSize: '16px' }}>{button?.quantity_str}</Text.Caption>
                                <Text.Heading className='mb-1' type='h4' variant='bold' style={{ fontWeight: '800' }}>{button?.cost}</Text.Heading>
                                <Text.Caption className='mb-1' color="#a0a0a0" style={{ fontSize: '13px' }}>{button?.cost_per_unit}/{units}</Text.Caption>
                                <div style={{ marginTop: '8px', marginBottom: '8px', marginRight: '16px' }}>
                                    <Text.Tiny style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                        <span style={{ fontWeight: '800' }}>{button?.subtitle}</span><br />
                                        based on your current driving habits.
                                    </Text.Tiny>
                                </div>
                                <Pill>
                                    <Text.Caption variant='bold'>Select</Text.Caption>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" viewBox="0 0 17 16" fill="none">
                                        <path d="M11.2774 8.66667L8.89073 11.06L9.83073 12L13.8307 8L9.83073 4L8.89073 4.94L11.2774 7.33333H3.16406V8.66667H11.2774Z" fill="white" />
                                    </svg>
                                </Pill>
                            </LinkButton>
                        </Col>
                    ))}
                </ButtonsWrapper>
          
                <BSRow>
                    <Col>
                        <Text.Heading variant='bold' type='h4' className='mb-4'>Purchase Credits ({units_plural})</Text.Heading>
                        <div style={{ position: 'relative', marginBottom: '32px' }}>
                            <Input 
                                disabled={!isCustomized || isLoading} 
                                type='number' 
                                inputStyle={{ 
                                    borderRadius: '8px',
                                    paddingTop: '38px',
                                    paddingBottom: '18px',
                                    height: 'auto',
                                    backgroundColor: isCustomized ? styles.colors.darkBackground : 'transparent',
                                    border: isCustomized ? 'none' : `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`
                                }} 
                                label={`Quantity (${units_plural})`} 
                                value={quantity} 
                                onChange={updateQuantity}
                            />
                            {!isCustomized && (
                                <div 
                                    style={{ 
                                        position: 'absolute', 
                                        right: '12px', 
                                        top: '50%', 
                                        transform: 'translateY(-50%)',
                                        zIndex: 10
                                    }}
                                >
                                    <CustomizeLink onClick={() => setIsCustomized(true)}>Customize</CustomizeLink>
                                </div>
                            )}
                        </div>
                        {!error && quantity && (
                            <React.Fragment>
                                <StyledRow style={{ borderBottom: `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`, padding: '16px 0' }}>
                                    <Text.Body variant='bold'>Price per {units} of driving</Text.Body>
                                    {isLoading || !quote || quote?.qty !== Number(quantity) ? (
                                        <SkeletonBox style={{ width: '80px', height: '20px' }} />
                                    ) : (
                                        <Text.Body>{quote?.unit_amount_str || '--'}</Text.Body>
                                    )}
                                </StyledRow>
                                <StyledRow style={{ borderBottom: `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`, padding: '16px 0' }}>
                                    <Text.Body variant='bold'>Account balance after purchase</Text.Body>
                                    {isLoading || !quote || quote?.qty !== Number(quantity) ? (
                                        <SkeletonBox style={{ width: '60px', height: '20px' }} />
                                    ) : (
                                        <Text.Body>{totalUnits} {totalUnits === 1 ? units : units_plural}</Text.Body>
                                    )}
                                </StyledRow>
                                {!isLoading && quote?.qty === Number(quantity) && !!quote?.promotion_subtotal && (
                                    <>
                                        <StyledRow style={{ borderBottom: `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`, padding: '16px 0' }}>
                                            <Text.Body variant='bold'>Regular Price </Text.Body>
                                            <Text.Body variant='bold'>{quote?.promotion_subtotal_str || '--'}</Text.Body>
                                        </StyledRow>
                                        <DiscountWrapper>
                                            <DiscountTab>
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M320 64C356.8 64 388.8 84.7 404.9 115.1C437.8 105 475 113 501 139C527 165 535 202.3 524.9 235.1C555.3 251.2 576 283.2 576 320C576 356.8 555.3 388.8 524.9 404.9C535 437.8 527 475 501 501C475 527 437.7 535 404.9 524.9C388.8 555.3 356.8 576 320 576C283.2 576 251.2 555.3 235.1 524.9C202.2 535 165 527 139 501C113 475 105 437.7 115.1 404.9C84.7 388.8 64 356.8 64 320C64 283.2 84.7 251.2 115.1 235.1C105 202.2 113 165 139 139C165 113 202.3 105 235.1 115.1C251.2 84.7 283.2 64 320 64zM256 288C273.7 288 288 273.7 288 256C288 238.3 273.7 224 256 224C238.3 224 224 238.3 224 256C224 273.7 238.3 288 256 288zM416 384C416 366.3 401.7 352 384 352C366.3 352 352 366.3 352 384C352 401.7 366.3 416 384 416C401.7 416 416 401.7 416 384zM401 273C410.4 263.6 410.4 248.4 401 239.1C391.6 229.8 376.4 229.7 367.1 239.1L239 367C229.6 376.4 229.6 391.6 239 400.9C248.4 410.2 263.6 410.3 272.9 400.9L401 273z"/></svg>
                                                SPECIAL OFFER APPLIED
                                            </DiscountTab>
                                            <DiscountContainer>
                                                <StyledRow>
                                                    <Text.Caption variant='bold'>{quote?.promotion_savings_description || '--'} </Text.Caption>
                                                    <Text.Caption variant='bold'> - {quote?.promotion_additional_savings_string || '--'} ({quote?.promotion_additional_savings_pct}%)</Text.Caption>
                                                </StyledRow>
                                            </DiscountContainer>
                                        </DiscountWrapper>
                                    </>
                                )}
                                <StyledRow style={{ borderBottom: `1px solid var(--tz-hairline-strong, rgba(255, 255, 255, 0.10))`, padding: '16px 0' }}>
                                    <Text.Body variant='bold'>Total Price</Text.Body>
                                    {isLoading || !quote || quote?.qty !== Number(quantity) ? (
                                        <SkeletonBox style={{ width: '100px', height: '20px' }} />
                                    ) : (
                                        <Text.Body variant='bold' color={!!quote?.promotion_subtotal ? styles.colors.founders : styles.colors.forest}>{quote?.quote_str || '--'}</Text.Body>
                                    )}
                                </StyledRow>
                            </React.Fragment>
                        )}
                        { !!error && (
                            <Text.Tiny color={styles.colors.error} style={{ marginTop: '12px' }}>{error}</Text.Tiny>
                        )}
                    </Col>
                    {/* <Col xs={12} md={6} lg={5}>
                        <PricesWrapper className='d-flex flex-column px-4 align-items-stretch'>
                            <StyledRow style={{ marginBottom: '12px' }} className='justify-content-between'>
                                <Text.Heading variant='bold' type='h5'>Auto Credit Refill</Text.Heading>
                                {isAutoloadLoading ? (
                                    <Spinner size={32} style={{ marginRight: '12px' }} />
                                ) : (
                                    <Switch checked={isRefill} onChange={updateIsRefill} disabled={isAutoloadLoading} />
                                )}
                            </StyledRow>
                            <Text.Caption color={styles.colors.lightGray} style={{ marginBottom: '12px' }}>We can top off your account as the credits start to run out. We will notify you as you approach your minimum that you set and only add your specified amount.</Text.Caption>
                            {isRefill && (
                                <React.Fragment>
                                    <StyledRow style={{ marginBottom: '12px' }}>
                                        <Input inputStyle={{ marginRight: '4px', borderRadius: '8px' }} label={`Refill Trigger (${units_plural})`} value={refillTrigger} onChange={updateRefillTrigger} />
                                        <Input inputStyle={{ marginLeft: '4px', borderRadius: '8px' }} label={`Refill Amount (${units_plural})`} value={refillAmount} onChange={updateRefillAmount} />
                                    </StyledRow>
                                    <StyledRow style={{ marginBottom: '12px' }}>
                                        <Text.Caption color={styles.colors.lightGray}>Refill Amount</Text.Caption>
                                        <Text.Caption color={styles.colors.forest}>${refillData.quote.quote_str}</Text.Caption>
                                    </StyledRow>
                                    {(refillTrigger !== refillData.threshold || refillAmount !== refillData.quantity) && (
                                        <Button onClick={updateAutoload}>Update</Button>
                                    )}
                                </React.Fragment>
                            )}
                            {!!refillError && (
                                <Text.Tiny color={styles.colors.error} style={{ marginTop: '12px' }}>{refillError}</Text.Tiny>
                            )}
                        </PricesWrapper>
                    </Col> */}
                </BSRow>
               
                {(() => {
                    const hasQuote = quote?.quote && quote?.qty === Number(quantity)
                    const isDisabled = isCheckoutLoading || !checkoutUrl || checkoutUrl?.quantity !== Number(quantity)
                    
                    // console.log('Button state:', {
                    //     hasQuote,
                    //     quantity: Number(quantity),
                    //     quoteQty: quote?.qty,
                    //     checkoutQty: checkoutUrl?.quantity,
                    //     isCheckoutLoading,
                    //     hasCheckoutUrl: !!checkoutUrl,
                    //     isDisabled
                    // })
                    
                        return hasQuote && (
                            isDisabled ? (
                                <StyledButtonDisabled style={{ marginTop: '32px' }}>
                                    {isCheckoutLoading ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Spinner size={24} style={{ marginRight: '12px' }} />
                                        </div>
                                    ) : (
                                        `Checkout ${quote?.quote_str || ''}`
                                    )}
                                </StyledButtonDisabled>
                            ) : (
                                <StyledButton href={checkoutUrl?.url} style={{ marginTop: '32px' }}>
                                    {`Checkout ${quote?.quote_str || ''}`}
                                </StyledButton>
                            )
                        )
                })()}
             
                
            </StyledContainer>
        </ScreenWrapper>
    )
}